import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import {
  getIsLoading,
  getSearch,
  getTotal,
  getList
} from 'redux/store/adminSplitFees/getters'
import { loading } from 'redux/store/adminSplitFees/slice'
import useInfiniteData from 'hooks/useInfiniteData'
import { getLedByGPOListActionAsync } from '../../../../redux/store/adminSplitFees/actions'
import { notification } from '../../../../components/Notification'
import { ILedByGPO } from '../../../../redux/store/adminSplitFees/types'
import { TFormEditFees } from './types'
import { editFeesGPO } from '../api'
import { useSplitFeesPopup } from '../../SplitFees/Providers/SplitFeesPopupProvider'
import { AxiosResponse } from 'axios'
import { useAppDispatch } from '../../../../redux/createStore'

type ContextProps = {
  state: {
    ledByGPOList: ILedByGPO[]
    search: string | null
    hasNextPage: boolean
    isLoading: boolean
  }
  actions: {
    handleLoadMore: VoidFunction
    setSearch: (value: string) => void
    editFees: (id: string, data: TFormEditFees) => void
  }
}

const LedByGPOContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const LedByGPOContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch()
  const _search = useSelector(getSearch)
  const total = useSelector(getTotal)
  const list = useSelector(getList)
  const isLoading = useSelector(getIsLoading)

  const [search, setSearch] = useState<string | null>(_search)
  const [ledByGPOList, setLedByGPOList] = useState<ILedByGPO[]>([])

  const timeoutRef = useRef<NodeJS.Timeout>()

  const { editLedByGPOPopup } = useSplitFeesPopup()

  const loadMore = useCallback(
    async (args) => {
      try {
        await dispatch(getLedByGPOListActionAsync({ ...args }))
      } catch (e) {
        const err = e as AxiosResponse
        console.error(err)
        notification.error({ message: err?.data.details })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, list]
  )

  useEffect(() => {
    setLedByGPOList(list)
  }, [list])

  const {
    limit,
    hasNextPage,
    handleLoadMore,
    reset,
    offset: currentOffset
  } = useInfiniteData({
    total,
    loadMore,
    limit: 20
  })

  useEffect(() => {
    dispatch(loading())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const _listenSearchChange = useCallback(() => {
    dispatch(
      getLedByGPOListActionAsync({
        limit,
        search: search || undefined
      })
    )
  }, [dispatch, limit, search])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(_listenSearchChange, 500)
    reset()
  }, [_listenSearchChange, reset])

  const editFees = useCallback(
    async (id: string, values: TFormEditFees) => {
      try {
        await editFeesGPO(id, values)
        editLedByGPOPopup.actions.close()
        dispatch(
          getLedByGPOListActionAsync({
            limit: limit + currentOffset,
            offset: 0,
            search: search ?? ''
          })
        )
      } catch (err) {
        console.error(err)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editLedByGPOPopup, limit, search, currentOffset]
  )

  const context = useMemo(
    () => ({
      state: {
        search,
        ledByGPOList,
        hasNextPage,
        isLoading
      },
      actions: {
        handleLoadMore,
        setSearch,
        editFees
      }
    }),
    [
      search,
      ledByGPOList,
      hasNextPage,
      handleLoadMore,
      setSearch,
      isLoading,
      editFees
    ]
  )

  return (
    <LedByGPOContext.Provider value={context}>
      {children}
    </LedByGPOContext.Provider>
  )
}

export const useLedByGPOListContext = () => useContext(LedByGPOContext)

export default LedByGPOContextProvider
